<template>
  <div class="document bg-main">
    <v-container fluid class="px-0">
      <v-alert
        v-if="!$online"
        dense
        tile
        color="gray"
        icon="mdi-alert-circle-outline"
        class="alert-offline text-headline"
      >
        {{ $t("t_no_connection") }}
      </v-alert>
      <v-row>
        <v-col class="px-2 mx-2">
          <v-card class="vh-100 bg-main" flat>
            <v-toolbar flat class="transparent px-0 py-2 pt-1 bar-top" height="30">
              <v-toolbar-title class="px-0">
                <v-btn
                  v-if="prevRoute && prevRoute.name"
                  :to="{ name: prevRoute.name }"
                  class="btn-secondary mx-0 mt-0"
                  small
                  ><v-icon>mdi-chevron-left</v-icon> {{ $t("t_back") }}</v-btn
                >
                <v-btn v-else @click="$router.go(-1)" class="btn-secondary mx-0 mt-0" small
                  ><v-icon>mdi-chevron-left</v-icon> {{ $t("t_back") }}</v-btn
                >
              </v-toolbar-title>
              <v-spacer></v-spacer>
            </v-toolbar>
            <div class="py-1" v-if="document">
              <v-card-title class="text-h5 px-3">{{ document.name }}</v-card-title>
              <v-card-subtitle class="text-subtitle-1 px-3" v-if="document.description">{{
                document.description
              }}</v-card-subtitle>
              <div class="px-3 pb-2" v-if="document.tags && document.tags.length">
                <tag-chips :tags="document.tags" :isSelectable="false" />
              </div>
              <v-card-text class="px-1">
                <v-list
                  flat
                  v-if="documentAssets && documentAssets.length"
                  :class="{ transparent: $vuetify.theme.dark }"
                >
                  <div v-for="(item, index) in documentAssets" :key="'document-' + index">
                    <DocumentAssetListItem
                      ref="documentAssetListItem"
                      :item="item"
                      :parent="'document'"
                      @getAsset="downloadAsset(item)"
                    />
                  </div>
                </v-list>
                <v-alert
                  v-else-if="documentAssets && documentAssets.length === 0 && !loading"
                  color="orange darken-1"
                  class="mx-3 my-4"
                  outlined
                >
                  <div class="d-flex align-center">
                    <v-icon size="48" class="mr-3" color="orange"> mdi-folder-open-outline </v-icon>
                    <div>
                      <div class="font-weight-medium mb-1">This document has no files</div>
                      <div class="text-caption">There are no files or attachments associated with this document.</div>
                    </div>
                  </div>
                </v-alert>
              </v-card-text>
            </div>
            <div v-if="showLoadMore" class="d-flex justify-center my-2">
              <v-progress-circular indeterminate color="primary" size="32" />
            </div>

            <div v-show="hasMore" ref="infiniteScrollTrigger" style="height: 1px; margin-top: 20px"></div>
            <h4 class="pa-3 mt-5" v-if="!loading && !document">{{ $t("v_document.t_no_document") }}</h4>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import Helpers from "@/mixins/helpers";
import DocumentAssetListItem from "@/components/documents/DocumentAssetListItem.vue";
import { DocumentService } from "@/services";
import { TagChips } from "@ranes-inc/shared-lib";

export default {
  name: "Document",
  metaInfo() {
    return {
      title: this.document?.name,
    };
  },
  mixins: [Helpers],
  components: { DocumentAssetListItem, TagChips },
  data() {
    return {
      loading: false,
      document: null,
      documentAssets: [],
      prevRoute: null,
      activeDocument: null,
      hasMore: true,
      documentsAssetsOrderByField: "description",
      documentAssetsAscending: false,
      documentAssetsSkipNum: 0,
      documentAssetsTakenum: 20,
      showLoadMore: false,
    };
  },
  methods: {
    async downloadAsset(documentAsset) {
      this.showLoadBar();
      let params = {};
      params.documentAssetId = documentAsset.id;
      try {
        const asset = await DocumentService.downloadAsset(params);
        this.activeDocument = asset.data;
        if (documentAsset.mimeType == "application/json") {
          fetch(this.activeDocument.downloadUrl)
            .then((response) => response.json())
            .then((data) => {
              const url = data.url;
              if (this.$platform !== "web") {
                window.location = url;
              } else {
                window.open(url, "_blank");
              }
            });
        } else if (this.activeDocument.downloadUrl) {
          if (this.$platform !== "web") {
            window.location = this.activeDocument.downloadUrl;
          } else {
            const blob = await this.getAssetFromDownloadUrl(this.activeDocument.downloadUrl, null, false);
            window.open(blob, "_blank");
          }
        }
        this.hideLoadBar();
      } catch {
        this.hideLoadBar();
      }
    },
    customSort(array, p, s) {
      return array.sort((a, b) => {
        // Check primary property
        if (!a[p] && b[p]) return 1;
        if (a[p] && !b[p]) return -1;
        if (a[p] && b[p]) {
          const primaryComparison = a[p].localeCompare(b[p]);
          if (primaryComparison !== 0) return primaryComparison;
        }
        // Check secondary property when primary is equal or both null
        if (!a[s] && b[s]) return 1;
        if (a[s] && !b[s]) return -1;
        if (a[s] && b[s]) {
          return a[s].localeCompare(b[s]);
        }
        return 0;
      });
    },
    // Loading document assets by adding pagination
    async loadDocumentAssets() {
      if (this.documentAssetsSkipNum === 0) {
        this.loading = true;
      } else {
        this.showLoadMore = true;
      }

      this.showLoadBar();

      const routeParams = this.$route.params;
      const params = {
        "paging.orderbyfield": this.documentsAssetsOrderByField,
        "paging.ascending": this.documentAssetsAscending,
        "paging.skipnum": this.documentAssetsSkipNum,
        "paging.takenum": this.documentAssetsTakenum,
        documentId: routeParams.id,
        showOtherUsers: false,
      };

      try {
        const r = await DocumentService.getDocumentAssets(params);
        const assets = r.data;

        assets.forEach((m) => {
          if (!this.documentAssets?.some((doc) => doc.id === m.id)) {
            this.documentAssets?.push(m);
          }
        });

        this.documentAssets = this.customSort(this.documentAssets, "description", "name");

        this.documentAssetsSkipNum = this.documentAssets.length;
        this.hasMore = !r.meta?.endOfResults;
      } finally {
        this.loading = false;
        this.showLoadMore = false;
        this.hideLoadBar();
      }
    },

    async init() {
      this.loading = true;
      const params = this.$route.params;
      this.showLoadBar();
      try {
        const doc = await DocumentService.getDocument(params.id);
        this.document = doc.data;
      } finally {
        this.loading = false;
        this.hideLoadBar();
      }
    },

    // Setup IntersectionObserver for infinite scroll.
    initInfiniteScroll() {
      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && this.hasMore && !this.showLoadMore) {
            this.loadDocumentAssets();
          }
        },
        { threshold: 1.0 },
      );

      if (this.$refs.infiniteScrollTrigger) {
        this.observer.observe(this.$refs.infiniteScrollTrigger);
      }
    },
  },

  mounted() {
    if (this.$online) {
      this.init();
      this.loadDocumentAssets();
      this.initInfiniteScroll();
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRoute = from;
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
};
</script>
<style lang="scss">
.bar-top > div {
  padding-left: 10px;
}
</style>
